// src/App.js
import React, { useState, useEffect } from "react";
import { Toaster } from "./components/ui/toaster";
import { ThemeProvider } from "./hooks/use-theme";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import AppShell from "./components/layout/app-shell";

// Pages
import HomePage from "./app/home/page";
import ProductsPage from "./app/products/page";
import CulinaryPartnersPage from "./app/culinary-partners/page";
import CulinaryPartnerDetailPage from "./app/culinary-partners/[partnerId]/page";
import CustomizationPage from "./app/customization/page";
import MoodBoardPage from "./app/mood-board/page";
import BookingPage from "./app/booking/page";
import SubscriptionPage from "./app/subscription/page";
import RegistryPage from "./app/registry/page";

// Auth Pages
import LoginPage from "./app/auth/login";
import SignupPage from "./app/auth/signup";

// Cart & Checkout
import CartPage from "./app/cart/page";
import CheckoutPage from "./app/checkout/page";

// Account & Admin
import AccountPage from "./app/account/page";
import AdminDashboard from "./app/admin/dashboard";
import SuppliersManagementPage from "./app/admin/suppliers";
import ProductCostsPage from "./app/admin/product-costs";

function AppRouter() {
  const [currentRoute, setCurrentRoute] = useState(() => {
    const hash = window.location.hash.replace('#/', '');
    return hash || 'home';
  });

  const [routeParams, setRouteParams] = useState({});

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#/', '');
      const parts = hash.split('/');

      // Parse routes with parameters
      if (parts[0] === 'culinary-partners' && parts[1]) {
        setCurrentRoute('culinary-partner-detail');
        setRouteParams({ partnerId: parts[1] });
      } else if (parts[0] === 'orders' && parts[1]) {
        setCurrentRoute('order-detail');
        setRouteParams({ orderId: parts[1] });
      } else {
        setCurrentRoute(hash || 'home');
        setRouteParams({});
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigate = (path) => {
    window.location.hash = `#/${path}`;
  };

  const renderPage = () => {
    // Auth pages (no AppShell)
    if (currentRoute === 'login') return <LoginPage />;
    if (currentRoute === 'signup') return <SignupPage />;

    // Pages with AppShell
    const PageContent = () => {
      switch (currentRoute) {
        case 'home':
          return <HomePage />;
        case 'products':
          return <ProductsPage />;
        case 'culinary-partners':
          return <CulinaryPartnersPage />;
        case 'culinary-partner-detail':
          return <CulinaryPartnerDetailPage partnerId={routeParams.partnerId} />;
        case 'customization':
          return <CustomizationPage />;
        case 'mood-board':
          return <MoodBoardPage />;
        case 'booking':
          return <BookingPage />;
        case 'subscription':
          return <SubscriptionPage />;
        case 'registry':
          return <RegistryPage />;
        case 'cart':
          return <CartPage />;
        case 'checkout':
          return <CheckoutPage />;
        case 'account':
          return <AccountPage />;
        case 'admin':
          return <AdminDashboard />;
        case 'admin/suppliers':
          return <SuppliersManagementPage />;
        case 'admin/product-costs':
          return <ProductCostsPage />;
        default:
          return <HomePage />;
      }
    };

    return (
      <AppShell currentRoute={currentRoute} navigate={navigate}>
        <PageContent />
      </AppShell>
    );
  };

  return (
    <>
      {renderPage()}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
